// Block renderers for dynamic pages
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { imgUrl, PropertyCard } from "./PropertyCard";
import { VideoPlayer } from "./VideoPlayer";
import { api } from "../lib/api";
import { Reveal, Stagger } from "./Reveal";
import {
  Check, ArrowRight, Star, MapPin, Sun, Zap, ShieldCheck, Layers, Truck,
  Building2, Leaf, Wallet, Award, Phone, ChevronLeft, ChevronRight,
} from "lucide-react";

const Wrap = ({ children, className = "" }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 md:px-8 ${className}`}>{children}</div>
);

// Icon library for why_us / territories blocks
const ICON_MAP = {
  Sun, Zap, ShieldCheck, Layers, Truck, Building2, Leaf, Wallet, Award, MapPin, Phone, Check,
};
const ICON_NAMES = Object.keys(ICON_MAP);

/**
 * Inline Markdown → safe HTML for block content (text, image_text, faq).
 * Supports **bold** and *italic*. Everything else is escaped, then sanitized
 * via DOMPurify with a strict allow-list to prevent XSS from CMS content.
 */
const escapeHtml = (s) => String(s ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const renderMd = (raw) => {
  if (!raw) return { __html: "" };
  let html = escapeHtml(raw);
  // **bold** → <strong>
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // *italic* (single asterisk, not at start of line to avoid bullets)
  html = html.replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>");
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["strong", "em", "br"],
    ALLOWED_ATTR: [],
  });
  return { __html: clean };
};

export const BLOCK_DEFS = {
  hero: { label: "Hero", defaults: { kicker: "", title: "Titre principal", subtitle: "Sous-titre descriptif", cta_label: "En savoir plus", cta_link: "/locaux", image_url: "" } },
  hero_full: { label: "Hero pleine largeur", defaults: { title: "Titre majeur", subtitle: "Texte", image_url: "" } },
  text: { label: "Section texte", defaults: { kicker: "", title: "Titre", content: "Votre texte ici. Les sauts de ligne sont préservés." } },
  image_text: { label: "Image + Texte", defaults: { title: "Titre", content: "Description...", image_url: "", image_side: "right" } },
  columns: { label: "Colonnes", defaults: { title: "Titre", count: 3, items: [{ title: "Col 1", content: "Texte" }, { title: "Col 2", content: "Texte" }, { title: "Col 3", content: "Texte" }] } },
  quote: { label: "Citation", defaults: { content: "Une citation marquante de votre métier.", author: "—" } },
  counters: { label: "Compteurs animés", defaults: { items: [{ value: "100", suffix: "+", label: "Clients" }, { value: "10", suffix: "ans", label: "Expérience" }] } },
  map: { label: "Carte (Google Maps)", defaults: { title: "Nous trouver", embed_url: "https://www.google.com/maps?q=Cercottes,+France&output=embed" } },
  gallery: { label: "Galerie images", defaults: { title: "Galerie", layout: 4, images: [] } },
  video: { label: "Vidéo", defaults: { title: "", video_url: "" } },
  cta: { label: "Bandeau CTA", defaults: { title: "Prêt à passer à l'étape suivante ?", subtitle: "Discutons-en.", cta_label: "Nous contacter", cta_link: "/contact" } },
  faq: { label: "FAQ", defaults: { title: "Questions fréquentes", items: [{ q: "Question 1 ?", a: "Réponse 1." }] } },
  features: { label: "Caractéristiques", defaults: { title: "Nos atouts", items: [{ title: "Atout 1", desc: "Description" }, { title: "Atout 2", desc: "Description" }] } },
  testimonials: { label: "Témoignages (auto)", defaults: { title: "Ils nous font confiance" } },
  partners: { label: "Partenaires (auto)", defaults: { title: "Nos partenaires" } },
  stats: { label: "Chiffres clés", defaults: { items: [{ value: "100%", label: "Engagement" }, { value: "24h", label: "Réponse" }] } },
  // ----- New Phase 9.2 -----
  recent_properties: {
    label: "Biens récents (auto)",
    defaults: {
      kicker: "Nos locaux",
      title: "Nos derniers locaux disponibles",
      subtitle: "Industriels, photovoltaïques et logistiques à Cercottes (Loiret).",
      count: 6,
      category: "all", // all | industriel | solaire | mixte
      display: "grid", // grid | slider
      show_cta: true,
      cta_label: "Voir tous les locaux",
      cta_link: "/locaux",
    },
  },
  territories: {
    label: "Territoires (cartes ville)",
    defaults: {
      kicker: "Notre zone",
      title: "Locaux d'activité dans le Loiret",
      subtitle: "À 25 min d'Orléans, en plein cœur du Centre-Val de Loire.",
      items: [
        { name: "Cercottes", desc: "Notre siège, 45520 — accès direct RN20", link: "/locaux", icon: "MapPin" },
        { name: "Orléans", desc: "À 25 min — bassin de 280 000 habitants", link: "/locaux", icon: "Building2" },
        { name: "Loiret", desc: "Locaux livrables dans tout le département", link: "/locaux", icon: "MapPin" },
        { name: "Centre-Val de Loire", desc: "Solutions sur-mesure pour la région", link: "/contact", icon: "MapPin" },
      ],
    },
  },
  why_us: {
    label: "Pourquoi nous (icônes)",
    defaults: {
      kicker: "Nos engagements",
      title: "Pourquoi choisir Immobilier Solaire",
      count: 4,
      items: [
        { icon: "Sun", title: "Photovoltaïque intégré", desc: "Toits équipés ou prêts à l'équipement, jusqu'à -40 % sur vos charges d'énergie." },
        { icon: "Layers", title: "Bâtiments modulables", desc: "Surfaces de 270 à 800 m², ajustables à vos contraintes métier." },
        { icon: "Truck", title: "Logistique optimisée", desc: "Quais PL, accès direct RN20, à 25 min d'Orléans." },
        { icon: "ShieldCheck", title: "Construction neuve", desc: "Normes RT 2020, isolation premium, sanitaires et bureau attenants." },
      ],
    },
  },
};

const Hero = ({ p }) => (
  <section className="relative overflow-hidden bg-slate-900 text-white py-24 md:py-32">
    {p.image_url && (<><img src={p.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" /><div className="absolute inset-0 bg-slate-900/50"/></>)}
    <Wrap className="relative">
      <Reveal>
        {p.kicker && <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-4">— {p.kicker}</div>}
        <h1 className="font-display font-black text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl">{p.title}</h1>
        {p.subtitle && <p className="text-lg md:text-xl text-slate-200 max-w-2xl mt-6">{p.subtitle}</p>}
      </Reveal>
      {p.cta_label && (
        <Reveal delay={200}>
          <Link to={p.cta_link || "/"} className="btn-brutal btn-brutal-solar inline-flex items-center gap-3 mt-8 px-8 py-4 font-bold uppercase tracking-wider text-sm group hover:-translate-y-0.5 transition-transform">
            {p.cta_label} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
          </Link>
        </Reveal>
      )}
    </Wrap>
  </section>
);

const TextBlock = ({ p }) => (
  <section className="py-12 md:py-24"><Wrap>
    <Reveal>
      {p.kicker && <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-4">— {p.kicker}</div>}
      {p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight mb-6 max-w-4xl">{p.title}</h2>}
      <div
        className="text-base md:text-lg text-slate-700 leading-relaxed whitespace-pre-line columns-1 lg:columns-2 lg:gap-12 [&_strong]:font-semibold [&_strong]:text-slate-900"
        dangerouslySetInnerHTML={renderMd(p.content)}
      />
    </Reveal>
  </Wrap></section>
);

const ImageText = ({ p }) => (
  <section className="py-12 md:py-24 bg-white border-y border-slate-900/10"><Wrap>
    <div className={`grid lg:grid-cols-2 gap-8 md:gap-12 items-center ${p.image_side === "left" ? "" : "lg:grid-flow-col-dense"}`}>
      <Reveal className={p.image_side === "left" ? "lg:order-1" : "lg:order-2"} y={32}>
        {p.image_url && <img src={p.image_url} alt="" loading="lazy" className="w-full aspect-[4/3] md:aspect-[4/5] object-cover border border-slate-900/15 hover:scale-[1.02] transition-transform duration-700" />}
      </Reveal>
      <Reveal className={p.image_side === "left" ? "lg:order-2" : "lg:order-1"} delay={120}>
        <h2 className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight mb-4 md:mb-6">{p.title}</h2>
        <div
          className="text-slate-700 leading-relaxed whitespace-pre-line text-sm md:text-base [&_strong]:font-semibold [&_strong]:text-slate-900"
          dangerouslySetInnerHTML={renderMd(p.content)}
        />
      </Reveal>
    </div>
  </Wrap></section>
);

const Gallery = ({ p }) => {
  const cols = Math.min(Math.max(parseInt(p.layout) || 4, 2), 5);
  const colCls = { 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-3 lg:grid-cols-4", 5: "md:grid-cols-3 lg:grid-cols-5" }[cols];
  return (
    <section className="py-12 md:py-24"><Wrap>
      <Reveal>{p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight mb-10">{p.title}</h2>}</Reveal>
      <div className={`grid grid-cols-2 ${colCls} gap-3`}>
        {(p.images || []).map((img, i) => (
          <Reveal key={(typeof img === "string" ? img : img.url) || `g-${i}`} delay={i * 60} y={20}>
            <div className="aspect-square overflow-hidden border border-slate-900/15 group">
              <img src={imgUrl(img)} alt={(typeof img === "object" && img.alt) || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" />
            </div>
          </Reveal>
        ))}
      </div>
    </Wrap></section>
  );
};

const detectVideoKind = (url) => {
  if (/youtu/.test(url)) return "youtube";
  if (/vimeo/.test(url)) return "vimeo";
  return "upload";
};

const VideoBlock = ({ p }) => (
  <section className="py-12 md:py-24 bg-slate-900"><Wrap>
    <Reveal>{p.title && <h2 className="font-display font-black text-white text-3xl md:text-5xl tracking-tight mb-10">{p.title}</h2>}</Reveal>
    <Reveal delay={120} y={32}>
      {p.video_url && <div className="aspect-video bg-black border border-slate-700"><VideoPlayer video={{ kind: detectVideoKind(p.video_url), url: p.video_url }} className="w-full h-full" controls /></div>}
    </Reveal>
  </Wrap></section>
);

const Cta = ({ p }) => (
  <section className="py-12 md:py-24 bg-slate-900 text-white"><Wrap>
    <div className="grid md:grid-cols-12 gap-8 items-center">
      <Reveal className="md:col-span-7">
        <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight">{p.title}</h2>
        {p.subtitle && <p className="text-slate-300 mt-4 max-w-xl">{p.subtitle}</p>}
      </Reveal>
      {p.cta_label && (
        <Reveal delay={150} className="md:col-span-5 flex md:justify-end">
          <Link to={p.cta_link || "/contact"} className="btn-brutal btn-brutal-solar px-10 py-5 font-bold uppercase tracking-wider inline-flex items-center gap-3 group hover:-translate-y-0.5 transition-transform">
            {p.cta_label} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform"/>
          </Link>
        </Reveal>
      )}
    </div>
  </Wrap></section>
);

const Faq = ({ p }) => (
  <section className="py-12 md:py-24"><Wrap>
    <Reveal>{p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight mb-10">{p.title}</h2>}</Reveal>
    <div className="space-y-3 max-w-3xl">
      {(p.items || []).map((it, i) => (
        <Reveal key={i} delay={i * 60} y={16}>
          <details className="bg-white border border-slate-900/15 p-5 group hover:border-slate-900 transition-colors">
            <summary className="font-semibold cursor-pointer flex items-center justify-between">{it.q}<span className="ml-4 text-[#FFC107] group-open:rotate-45 transition-transform">+</span></summary>
            <p
              className="mt-3 text-slate-700 leading-relaxed [&_strong]:font-semibold [&_strong]:text-slate-900"
              dangerouslySetInnerHTML={renderMd(it.a)}
            />
          </details>
        </Reveal>
      ))}
    </div>
  </Wrap></section>
);

const Features = ({ p }) => (
  <section className="py-12 md:py-24 bg-white border-y border-slate-900/10"><Wrap>
    <Reveal>{p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight mb-12 text-center">{p.title}</h2>}</Reveal>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      {(p.items || []).map((it, i) => (
        <Reveal key={i} delay={i * 90} y={28}>
          <div className="bg-[#F9F9F7] border border-slate-900/15 p-8 hover:-translate-y-1 hover:shadow-lg hover:border-slate-900 transition-all duration-300 h-full">
            <div className="w-10 h-10 bg-[#FFC107] border border-slate-900 flex items-center justify-center mb-4"><Check className="w-5 h-5" strokeWidth={3}/></div>
            <div className="font-display font-bold text-xl mb-2">{it.title}</div>
            <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Wrap></section>
);

// Static maps to keep Tailwind JIT happy with dynamic column counts
const STAT_COLS = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" };

const Stats = ({ p }) => {
  const n = Math.min((p.items || []).length || 1, 4);
  return (
    <section className="border-y border-slate-900 bg-slate-900">
      {/* `gap-px bg-slate-900` + `bg-white` on each cell creates a clean 1px grid
          that works for ALL layouts (2×2 mobile, 1×N desktop) — fixes the missing
          horizontal divider issue on mobile 2-row stacks. */}
      <div className={`max-w-7xl mx-auto grid grid-cols-2 ${STAT_COLS[n]} gap-px`}>
        {(p.items || []).map((it, i) => (
          <Reveal key={i} delay={i * 100} y={16}>
            <div className="p-5 md:p-8 text-center bg-white lg:hover:bg-[#FFC107]/5 transition-colors duration-300 h-full">
              <div className="font-display font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight break-words">{it.value}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 font-semibold mt-2">{it.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Testimonials = ({ p, testimonials = [] }) => (
  <section className="py-12 md:py-24"><Wrap>
    <Reveal>{p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight mb-12">{p.title}</h2>}</Reveal>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      {testimonials.map((t, i) => (
        <Reveal key={i} delay={i * 90} y={28}>
          <div className="bg-white border border-slate-900/15 p-8 flex flex-col h-full hover:border-slate-900 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
            <div className="flex gap-1 mb-4">{Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} className="w-4 h-4 text-[#FFC107]" fill="currentColor" />)}</div>
            <p className="text-slate-700 leading-relaxed flex-1">&ldquo;{t.content}&rdquo;</p>
            <div className="mt-6 pt-4 border-t border-slate-900/10">
              <div className="font-display font-bold">{t.name}</div>
              <div className="text-xs text-slate-500">{[t.role, t.company].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </Wrap></section>
);

const Partners = ({ p, partners = [] }) => (
  <section className="py-16 bg-white border-y border-slate-900/10"><Wrap>
    <Reveal>{p.title && <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-8 text-center text-slate-500">{p.title}</h2>}</Reveal>
    <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
      {partners.map((pa, i) => (
        <Reveal key={i} delay={i * 70} y={12}>
          {pa.logo_url ? (
            pa.website ? <a href={pa.website} target="_blank" rel="noreferrer" className="opacity-70 hover:opacity-100 transition-opacity"><img src={pa.logo_url} alt={pa.name} loading="lazy" className="h-12 object-contain"/></a>
            : <img src={pa.logo_url} alt={pa.name} loading="lazy" className="h-12 object-contain opacity-70"/>
          ) : <span className="font-display font-bold text-lg text-slate-700">{pa.name}</span>}
        </Reveal>
      ))}
    </div>
  </Wrap></section>
);

export const BlockRenderer = ({ block, testimonials, partners }) => {
  if (block.hidden) return null;
  const p = block.props || {};
  switch (block.type) {
    case "hero": return <Hero p={p} />;
    case "hero_full": return <HeroFull p={p} />;
    case "text": return <TextBlock p={p} />;
    case "image_text": return <ImageText p={p} />;
    case "columns": return <Columns p={p} />;
    case "quote": return <Quote p={p} />;
    case "counters": return <Counters p={p} />;
    case "map": return <MapBlock p={p} />;
    case "gallery": return <Gallery p={p} />;
    case "video": return <VideoBlock p={p} />;
    case "cta": return <Cta p={p} />;
    case "faq": return <Faq p={p} />;
    case "features": return <Features p={p} />;
    case "stats": return <Stats p={p} />;
    case "testimonials": return <Testimonials p={p} testimonials={testimonials} />;
    case "partners": return <Partners p={p} partners={partners} />;
    case "recent_properties": return <RecentProperties p={p} />;
    case "territories": return <Territories p={p} />;
    case "why_us": return <WhyUs p={p} />;
    default: return null;
  }
};

// ----- Phase 9.2 — Recent properties (auto-fetched) -----
const RecentProperties = ({ p }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/properties").then(r => setItems(r.data)).catch(() => setItems([])); }, []);
  const filtered = useMemo(() => {
    const max = Math.max(parseInt(p.count) || 6, 1);
    return (items || [])
      .filter(it => it.available !== false)
      .filter(it => p.category && p.category !== "all" ? it.category === p.category : true)
      .slice(0, max);
  }, [items, p.count, p.category]);
  const isSlider = p.display === "slider";
  return (
    <section className="py-12 md:py-24"><Wrap>
      <Reveal>
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            {p.kicker && <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-3">— {p.kicker}</div>}
            {p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight max-w-2xl">{p.title}</h2>}
            {p.subtitle && <p className="text-base md:text-lg text-slate-600 mt-3 max-w-xl">{p.subtitle}</p>}
          </div>
          {p.show_cta !== false && p.cta_label && (
            <Link to={p.cta_link || "/locaux"} className="text-sm font-bold uppercase tracking-wider hover:text-[#FFC107] inline-flex items-center gap-2 group">
              {p.cta_label} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
            </Link>
          )}
        </div>
      </Reveal>
      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500 font-mono">Aucun local disponible pour le moment.</div>
      ) : isSlider ? (
        <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory -mx-4 px-4 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8 scrollbar-thin scrollbar-thumb-slate-300">
          {filtered.map((prop, i) => (
            <Reveal key={prop.id} delay={i * 80} y={28} className="min-w-[260px] sm:min-w-[300px] md:min-w-[360px] snap-start">
              <PropertyCard property={prop} />
            </Reveal>
          ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filtered.map((prop, i) => (
            <Reveal key={prop.id} delay={i * 80} y={28}><PropertyCard property={prop} /></Reveal>
          ))}
        </div>
      )}
    </Wrap></section>
  );
};

// ----- Phase 9.2 — Territories cards -----
const Territories = ({ p }) => (
  <section className="py-12 md:py-24 bg-white border-y border-slate-900/10"><Wrap>
    <Reveal>
      <div className="mb-12 max-w-2xl">
        {p.kicker && <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-3">— {p.kicker}</div>}
        {p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight">{p.title}</h2>}
        {p.subtitle && <p className="text-base md:text-lg text-slate-600 mt-3">{p.subtitle}</p>}
      </div>
    </Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
      {(p.items || []).map((it, i) => {
        const Icon = ICON_MAP[it.icon] || MapPin;
        const inner = (
          <div className="bg-[#F9F9F7] border border-slate-900/15 p-7 h-full hover:border-slate-900 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group">
            <div className="w-12 h-12 bg-[#FFC107] border border-slate-900 flex items-center justify-center mb-5 group-hover:rotate-3 group-hover:scale-110 transition-transform duration-300"><Icon className="w-5 h-5" strokeWidth={2.4}/></div>
            <div className="font-display font-black text-2xl mb-2">{it.name}</div>
            <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            {it.link && <div className="mt-4 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 group-hover:text-[#FFC107] group-hover:gap-2 transition-all">Voir <ArrowRight className="w-3 h-3"/></div>}
          </div>
        );
        return (
          <Reveal key={`terr-${i}`} delay={i * 100} y={24}>
            {it.link ? <Link to={it.link} className="block h-full">{inner}</Link> : inner}
          </Reveal>
        );
      })}
    </div>
  </Wrap></section>
);

// ----- Phase 9.2 — Why us (icon grid) -----
const WhyUs = ({ p }) => {
  const cols = Math.min(Math.max(parseInt(p.count) || 4, 2), 6);
  const colCls = { 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-2 lg:grid-cols-4", 5: "md:grid-cols-3 lg:grid-cols-5", 6: "md:grid-cols-3 lg:grid-cols-6" }[cols];
  return (
    <section className="py-12 md:py-24"><Wrap>
      <Reveal>
        <div className="mb-12 max-w-2xl">
          {p.kicker && <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-3">— {p.kicker}</div>}
          {p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight">{p.title}</h2>}
        </div>
      </Reveal>
      <div className={`grid grid-cols-1 ${colCls} gap-6`}>
        {(p.items || []).map((it, i) => {
          const Icon = ICON_MAP[it.icon] || Check;
          return (
            <Reveal key={`why-${i}`} delay={i * 90} y={24}>
              <div className="border border-slate-900/15 bg-white p-7 hover:bg-[#FFC107]/5 hover:border-slate-900 hover:-translate-y-1 hover:shadow-md transition-all duration-300 h-full group">
                <div className="w-11 h-11 bg-slate-900 text-[#FFC107] flex items-center justify-center mb-5 group-hover:rotate-6 transition-transform duration-300"><Icon className="w-5 h-5" strokeWidth={2.4}/></div>
                <div className="font-display font-bold text-lg mb-2">{it.title}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </Wrap></section>
  );
};

export { ICON_NAMES };

const HeroFull = ({ p }) => (
  <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-center justify-center text-white overflow-hidden">
    {p.image_url ? (
      <><img src={p.image_url} alt="" className="absolute inset-0 w-full h-full object-cover scale-105 hover:scale-100 transition-transform duration-[2000ms]"/><div className="absolute inset-0 bg-slate-900/55"/></>
    ) : (
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"/>
    )}
    <Reveal className="relative text-center px-6 max-w-4xl" y={40}>
      <h1 className="font-display font-black text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95]">{p.title}</h1>
      {p.subtitle && <p className="text-base sm:text-lg md:text-xl text-slate-200 mt-5 md:mt-6 max-w-2xl mx-auto">{p.subtitle}</p>}
    </Reveal>
  </section>
);

const ColumnsCard = ({ it }) => (
  <div className={`bg-[color:var(--bg-surface)] border border-slate-900/15 p-6 hover:-translate-y-1 hover:shadow-md hover:border-slate-900 transition-all duration-300 h-full ${it.link ? "cursor-pointer" : ""}`}>
    <div className="font-display font-bold text-xl mb-3 flex items-center justify-between gap-2">
      <span>{it.title}</span>
      {it.link && <span aria-hidden className="text-[color:var(--solar)] text-2xl leading-none">→</span>}
    </div>
    <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "var(--text-secondary)" }}>{it.content}</p>
  </div>
);

const Columns = ({ p }) => {
  const count = p.count || 3;
  const items = p.items || [];
  const gridCls = count === 2 ? "md:grid-cols-2" : count === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3";
  return (
    <section className="py-12 md:py-24"><Wrap>
      <Reveal>{p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight mb-12">{p.title}</h2>}</Reveal>
      <div className={`grid ${gridCls} gap-6`}>
        {items.map((it, i) => (
          <Reveal key={i} delay={i * 80} y={20}>
            {it.link ? (
              <a href={it.link} className="block no-underline text-current"><ColumnsCard it={it} /></a>
            ) : (
              <ColumnsCard it={it} />
            )}
          </Reveal>
        ))}
      </div>
    </Wrap></section>
  );
};

const Quote = ({ p }) => (
  <section className="py-12 md:py-24"><Wrap>
    <Reveal y={32}>
      <blockquote className="max-w-4xl mx-auto text-center">
        <div className="font-display text-3xl md:text-5xl font-black leading-tight">&ldquo;{p.content}&rdquo;</div>
        <div className="mt-6 text-sm font-bold uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>{p.author}</div>
      </blockquote>
    </Reveal>
  </Wrap></section>
);

const Counters = ({ p }) => {
  const n = Math.min((p.items || []).length || 1, 4);
  return (
    <section className="py-12 md:py-20 border-y border-slate-900">
      <div className={`max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 ${STAT_COLS[n]} divide-x divide-slate-900`}>
        {(p.items || []).map((it, i) => (
          <Reveal key={i} delay={i * 100} y={16}>
            <div className="p-4 md:p-6 text-center hover:bg-[#FFC107]/5 transition-colors duration-300">
              <div className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight break-words">
                <span style={{ color: "var(--text-primary)" }}>{it.value}</span>
                {it.suffix && <span className="text-[color:var(--solar)]"> {it.suffix}</span>}
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold mt-2" style={{ color: "var(--text-secondary)" }}>{it.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const MapBlock = ({ p }) => (
  <section className="py-12 md:py-24"><Wrap>
    <Reveal>{p.title && <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl tracking-tight mb-8">{p.title}</h2>}</Reveal>
    <Reveal delay={120} y={28}>
      {p.embed_url && (
        <div className="border border-slate-900/15 overflow-hidden">
          <iframe src={p.embed_url} className="w-full h-[420px] block" style={{ border: 0 }} loading="lazy" allowFullScreen title="Carte"/>
        </div>
      )}
    </Reveal>
  </Wrap></section>
);
